import { Form, Input } from "semantic-ui-react";
import { SharedAddressFieldProps } from "./AddressFormField";
import { AccessibleDropdown } from "../accessible/AccessibleDropdown";
import useCountries from "../../../../hooks/useCountries";
import classNames from "classnames";
import { useId } from "react-aria";

interface StateFormFieldProps extends SharedAddressFieldProps {
  countryId: string;
  subLabelPadding?: boolean;
}

export function StateFormField({
  required,
  disabled,
  error,
  labelClassName,
  label,
  subLabel,
  value,
  transform,
  handleChange,
  subLabelPadding,
  countryId,
  isMobileLayout,
}: StateFormFieldProps) {
  const id = useId();
  const { countries } = useCountries();
  const countryData = countries[countryId];

  const stateOptions =
    countryData?.states?.map((state: string) => ({
      key: state,
      text: state,
      value: state,
      label: state,
      disabled: false,
    })) ?? null;

  // Add the default state to the options list if it's not already there (this is to handle dirty data in the database,
  // as we don't want the field to appear blank. But the user will still have to select a valid state to submit the form)
  if (value?.state && stateOptions && !stateOptions.find((option: { key: string }) => option.key === value.state)) {
    stateOptions.push({
      key: value.state,
      text: value.state,
      value: value.state,
      label: value.state,
      disabled: true,
    });
  }

  return (
    <Form.Field
      className={classNames("tw-w-auto tw-text-nowrap", {
        "tw-mb-4": isMobileLayout,
      })}
      required={required}
      disabled={disabled}
      error={!!error}
    >
      <label htmlFor={id} className={labelClassName}>
        {label || (countryId === "US" ? "State" : "State/Province")}
      </label>
      {(subLabel || subLabelPadding) && (
        <div
          className={classNames("sub-label", {
            "tw-text-[#b2b2b2]": disabled,
          })}
        >
          {subLabel}
        </div>
      )}
      {/* Provide dropdown options if they exist in the database, otherwise make it a free text field */}
      {stateOptions ? (
        <AccessibleDropdown
          classNamePrefix='address-state-dropdown'
          id={id}
          clearable={true}
          options={stateOptions}
          value={stateOptions.find((option: any) => option.value === value?.state) ?? null}
          onChange={(newValue: any) => handleChange({ state: newValue?.value ?? "" })}
          disabled={disabled}
          placeholder=''
          error={error}
          showErrorLabel={false}
        />
      ) : (
        <Input
          id={id}
          type='text'
          value={transform.input(value)?.state ?? ""}
          onChange={(_, { value }) =>
            handleChange({
              state: value
                .toUpperCase()
                .trim()
                .slice(0, 3)
                .replace(/[^A-Z]/g, ""),
            })
          }
          disabled={disabled}
          autoComplete='address-level1'
        />
      )}
    </Form.Field>
  );
}
